import { clamp } from './math';
import { Timeline } from './timeline';
import type { Effects } from './effects';
import type { PageAdapter } from './page';
import type { Actor, Motion, PlayfulAction, Point, Pose, Surface } from './types';

interface RoutineContext {
  hero: () => Actor;
  floor: () => number;
  interacts: () => boolean;
  fitX: (x: number) => number;
  pose: (pose: Pose) => void;
  move: (x: number, y: number, jumping: boolean, surface?: Surface) => Motion;
  complete: () => void;
}

export interface WallRoute {
  surface: Surface;
  side: 'left' | 'right';
  x: number;
}

/** Only walls with enough visible space for the whole character are eligible. */
export function chooseWall(
  surfaces: readonly Surface[],
  actor: Actor,
  width: number,
  floor: number,
): WallRoute | undefined {
  const routes: WallRoute[] = [];
  for (const surface of surfaces) {
    if (
      surface.height < 75 ||
      surface.width < actor.size * 0.6 ||
      surface.y < actor.size * 0.9 ||
      surface.y + surface.height > floor
    )
      continue;
    if (surface.x >= actor.size * 0.85) routes.push({ surface, side: 'left', x: surface.x });
    const right = surface.x + surface.width;
    if (right <= width - actor.size * 0.85) routes.push({ surface, side: 'right', x: right });
  }
  return routes.sort(
    (a, b) =>
      Math.hypot(a.x - actor.x, a.surface.y + a.surface.height - actor.y) -
      Math.hypot(b.x - actor.x, b.surface.y + b.surface.height - actor.y),
  )[0];
}

/** Self-contained choreographies; all deferred work is cancellable and pauses with the engine. */
export class Routines {
  private timeline = new Timeline();
  private active: PlayfulAction | undefined;
  private wallElement: HTMLElement | undefined;
  private ghostTime = 0;

  constructor(
    private context: RoutineContext,
    private page: PageAdapter,
    private effects: Effects,
  ) {}

  get canWallRun(): boolean {
    return (
      this.context.interacts() &&
      !!chooseWall(
        this.page.surfaces,
        this.context.hero(),
        this.page.bounds.width,
        this.context.floor(),
      )
    );
  }

  start(action: PlayfulAction, pointer?: Point): void {
    this.active = action;
    this.ghostTime = 0;
    switch (action) {
      case 'kawarimi':
        this.kawarimi();
        break;
      case 'dodge':
        this.dodge(pointer);
        break;
      case 'ramen':
        this.ramen();
        break;
      case 'wallrun':
        this.wallrun();
        break;
      case 'sexyjutsu':
        this.transformation();
        break;
    }
  }

  private finish = (): void => {
    this.active = undefined;
    this.wallElement = undefined;
    this.context.hero().rotation = 0;
    this.context.hero().opacity = 1;
    this.context.complete();
  };

  cancel(): void {
    this.timeline.clear();
    if (!this.active) return;
    const hero = this.context.hero();
    hero.motion = undefined;
    hero.surface = undefined;
    hero.y = this.context.floor();
    this.context.pose('idle');
    this.finish();
  }

  update(dt: number): void {
    if (this.wallElement && !this.page.measureSurface(this.wallElement)) this.cancel();
    this.timeline.update(dt);
    const hero = this.context.hero();
    if (hero.motion && (hero.pose === 'dodge' || hero.pose === 'wallrun')) {
      this.ghostTime += dt;
      if (this.ghostTime >= 0.055) {
        this.ghostTime = 0;
        this.effects.afterimage(hero);
      }
    }
  }

  private supportAt(x: number): Surface | undefined {
    const support = this.page.measureSurface(this.context.hero().surface);
    const margin = this.context.hero().size * 0.25;
    return support && x >= support.x + margin && x <= support.x + support.width - margin
      ? support
      : undefined;
  }

  private destination(awayFrom?: number): number {
    const hero = this.context.hero();
    const direction = hero.x >= (awayFrom ?? this.page.bounds.width / 2) ? 1 : -1;
    const distance = hero.size * 1.6;
    let x = this.context.fitX(hero.x + direction * distance);
    // At the edge, dodge toward free space instead of getting stuck against the boundary.
    if (Math.abs(x - hero.x) < hero.size * 0.65)
      x = this.context.fitX(hero.x - direction * distance);
    return x;
  }

  private kawarimi(): void {
    const hero = this.context.hero();
    this.context.pose('seal');
    this.timeline.after(0.22, () => {
      const support = this.page.measureSurface(hero.surface);
      this.effects.smoke(hero.x, hero.y, hero.size / 120);
      this.effects.logs.push({
        x: hero.x,
        y: hero.y,
        size: hero.size * 0.8,
        age: 0,
        life: 3.2,
        surface: support?.element,
        offsetX: hero.x - (support?.x ?? 0),
      });
      hero.opacity = 0;
    });
    this.timeline.after(0.52, () => {
      const x = this.destination();
      const support = this.supportAt(x);
      hero.x = x;
      hero.y = support?.y ?? this.context.floor();
      hero.surface = support?.element;
      hero.opacity = 1;
      this.context.pose('land');
      this.effects.smoke(hero.x, hero.y, hero.size / 140);
    });
    this.timeline.after(0.9, this.finish);
  }

  private dodge(pointer?: Point): void {
    this.context.pose('land');
    this.timeline.after(0.12, () => {
      const x = this.destination(pointer?.x);
      const support = this.supportAt(x);
      const motion = this.context.move(x, support?.y ?? this.context.floor(), true, support);
      motion.duration = 0.58;
      motion.arc = 48;
      motion.onComplete = this.finish;
      this.context.pose('dodge');
    });
  }

  private ramen(): void {
    const hero = this.context.hero();
    const eat = () => {
      this.context.pose('ramen');
      this.timeline.after(5.2, () => {
        this.context.pose('land');
        this.finish();
      });
    };
    const current = this.page.measureSurface(hero.surface);
    const surfaces = this.context.interacts()
      ? this.page.surfaces.filter(
          (s) =>
            s.width >= hero.size * 0.7 &&
            s.y > hero.size &&
            s.x + s.width > hero.size * 0.5 &&
            s.x < this.page.bounds.width - hero.size * 0.5,
        )
      : [];
    const support =
      current ??
      surfaces.sort(
        (a, b) => Math.abs(a.x + a.width / 2 - hero.x) - Math.abs(b.x + b.width / 2 - hero.x),
      )[0];
    if (support && support.element !== hero.surface) {
      const motion = this.context.move(
        this.context.fitX(support.x + support.width / 2),
        support.y,
        true,
        support,
      );
      motion.onComplete = eat;
    } else eat();
  }

  /** A fully clothed comic transformation; smoke bookends the dedicated sprite cycle. */
  private transformation(): void {
    const hero = this.context.hero();
    const poof = () => {
      this.effects.smoke(hero.x, hero.y, hero.size / 155);
      this.effects.smoke(hero.x, hero.y - hero.size * 0.48, hero.size / 180);
      hero.opacity = 0;
    };
    this.context.pose('seal');
    this.timeline.after(0.35, poof);
    this.timeline.after(0.55, () => {
      this.context.pose('transform');
      hero.opacity = 1;
    });
    for (const at of [0.9, 1.65, 2.4])
      this.timeline.after(at, () => {
        this.effects.sparkles(hero.x, hero.y - hero.size * 0.55, hero.size);
      });
    this.timeline.after(3.5, poof);
    this.timeline.after(3.7, () => {
      hero.opacity = 1;
      this.context.pose('land');
    });
    this.timeline.after(4, this.finish);
  }

  private wallrun(): void {
    const hero = this.context.hero();
    const route = chooseWall(
      this.page.surfaces,
      hero,
      this.page.bounds.width,
      this.context.floor(),
    );
    if (!route) {
      this.finish();
      return;
    }
    const { surface, side, x } = route;
    this.wallElement = surface.element;
    const approach = this.context.move(x, surface.y + surface.height - 8, true);
    approach.duration = clamp(approach.duration, 0.4, 0.8);
    approach.arc = 35;
    approach.wall = { element: surface.element, side, phase: 'approach' };
    approach.onComplete = () => {
      const wall = this.page.measureSurface(surface.element);
      if (!wall) {
        this.cancel();
        return;
      }
      const edge = side === 'left' ? wall.x : wall.x + wall.width;
      const climb = this.context.move(edge, wall.y + 12, false);
      climb.duration = clamp(wall.height / 160, 0.7, 1.5);
      climb.wall = { element: wall.element, side, phase: 'climb' };
      hero.rotation = side === 'left' ? -Math.PI / 2 : Math.PI / 2;
      hero.facing = side === 'left' ? 1 : -1;
      this.context.pose('wallrun');
      climb.onComplete = () => {
        const top = this.page.measureSurface(surface.element);
        if (!top) {
          this.cancel();
          return;
        }
        hero.rotation = 0;
        const inset = Math.min(hero.size * 0.6, top.width / 2);
        const landingX = side === 'left' ? top.x + inset : top.x + top.width - inset;
        const landing = this.context.move(landingX, top.y, true, top);
        landing.duration = 0.55;
        landing.arc = 52;
        landing.origin = { element: top.element, offsetX: hero.x - top.x, offsetY: 12 };
        landing.onComplete = this.finish;
      };
    };
  }
}
